import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const TRACKER_URL = process.env.REACT_APP_TRACKER_URL || 'http://localhost:5002';

function getSessionId() {
  let sid = sessionStorage.getItem('_tid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('_tid', sid);
  }
  return sid;
}

function getToken() {
  return localStorage.getItem('token') || '';
}

function viewingAs() {
  try { return !!localStorage.getItem('ge.viewAs'); } catch (e) { return false; }
}

export function sendEvent(eventType, opts) {
  if (viewingAs()) return; // God's Eye "view as": a superadmin looking, not the person using the app
  var options = opts || {};
  try {
    fetch(TRACKER_URL + '/track/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + getToken(),
      },
      body: JSON.stringify(Object.assign({
        session_id: getSessionId(),
        event_type: eventType,
        page: window.location.pathname,
      }, options)),
      keepalive: true,
    });
  } catch(e) {}
}

/* Get readable text from an element — tries multiple strategies */
function getLabel(el) {
  if (!el) return '';
  // aria-label is most intentional
  var a = el.getAttribute('aria-label');
  if (a) return a.slice(0, 80);
  // title
  var t = el.getAttribute('title');
  if (t) return t.slice(0, 80);
  // Direct text (skip if too long — means it's a container)
  var text = '';
  // Only get direct text nodes + immediate child spans, not deep nested content
  for (var i = 0; i < el.childNodes.length; i++) {
    var node = el.childNodes[i];
    if (node.nodeType === 3) text += node.textContent; // text node
    else if (node.tagName === 'SPAN' || node.tagName === 'P' || node.tagName === 'DIV') {
      text += (node.innerText || '');
    }
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (text && text.length <= 80) return text;
  if (text) return text.slice(0, 77) + '...';
  // value for inputs
  var v = el.getAttribute('value') || el.getAttribute('name') || el.getAttribute('data-value');
  if (v) return v.slice(0, 80);
  return '';
}

/* For icon-only buttons, look at tooltip or surrounding context */
function getButtonLabel(btn) {
  var label = getLabel(btn);
  if (label && label !== '(icon)') return label;

  // Check if parent is a Tooltip — MUI wraps with a span that has title
  var parent = btn.parentElement;
  if (parent) {
    var pTitle = parent.getAttribute('title') || parent.getAttribute('aria-label');
    if (pTitle) return pTitle.slice(0, 80);
  }

  // Check SVG icon — try data-testid or class name
  var svg = btn.querySelector('svg');
  if (svg) {
    var testId = svg.getAttribute('data-testid') || '';
    if (testId) return testId.replace(/Icon$/, '').replace(/([A-Z])/g, ' $1').trim();
    // Try the MUI icon class: MuiSvgIcon-root, often has a second class
    var cls = svg.getAttribute('class') || '';
    var match = cls.match(/Mui(\w+)-root/);
    if (match && match[1] !== 'SvgIcon') return match[1];
  }

  // Last resort: find nearby label text
  var card = btn.closest('.MuiCard-root, .MuiPaper-root, section');
  if (card) {
    var heading = card.querySelector('h1,h2,h3,h4,h5,h6,[class*="title"],[class*="label"]');
    if (heading) {
      var hText = (heading.innerText || '').trim().slice(0, 40);
      if (hText) return '(' + hText + ')';
    }
  }

  return '';
}

/* Identify what section of the page the click happened in */
function getSection(el) {
  // Named section
  var tracked = el.closest('[data-track-component]');
  if (tracked) return tracked.dataset.trackComponent;
  // Page path as section
  var page = window.location.pathname.replace('/app/', '').split('/')[0];
  return page || 'home';
}

var lastClick = 0;
var lastKey = '';

function handleClick(e) {
  var el = e.target;

  // 1. Explicit data-track
  var tracked = el.closest('[data-track-component]');
  if (tracked) {
    sendEvent('click', {
      component: tracked.dataset.trackComponent,
      action: tracked.dataset.trackAction || 'click',
      metadata: { label: getLabel(tracked) },
    });
    return;
  }

  // 2. Tabs
  var tab = el.closest('[role="tab"]');
  if (tab) {
    var tabLabel = getLabel(tab);
    sendEvent('click', {
      component: 'tab',
      action: tabLabel || 'switch',
      metadata: { label: tabLabel, section: getSection(tab) },
    });
    return;
  }

  // 3. Select / dropdown menu items
  var menuItem = el.closest('[role="option"], .MuiMenuItem-root');
  if (menuItem) {
    sendEvent('click', {
      component: 'select',
      action: getLabel(menuItem),
      metadata: { label: getLabel(menuItem), section: getSection(menuItem) },
    });
    return;
  }

  // 4. Buttons
  var btn = el.closest('button');
  if (btn) {
    var btnLabel = getButtonLabel(btn);
    var section = getSection(btn);
    var key = section + ':' + btnLabel;

    // Throttle duplicate
    var now = Date.now();
    if (now - lastClick < 400 && key === lastKey) return;
    lastClick = now;
    lastKey = key;

    // Skip noise: scroll buttons, ripple clicks, empty unlabeled
    if (!btnLabel) return;

    sendEvent('click', {
      component: section,
      action: btnLabel,
      metadata: { element: 'button', label: btnLabel },
    });
    return;
  }

  // 5. Links
  var link = el.closest('a[href]');
  if (link) {
    var href = link.getAttribute('href') || '';
    if (href === '#' || href.startsWith('javascript:')) return;
    var linkLabel = getLabel(link) || href.split('/').pop() || href;
    sendEvent('click', {
      component: getSection(link),
      action: href.startsWith('http') ? 'external: ' + linkLabel : linkLabel,
      metadata: { element: 'link', href: href.slice(0, 100), label: linkLabel },
    });
    return;
  }

  // 6. Clickable table rows / chips
  var clickable = el.closest('tr, .MuiChip-root');
  if (clickable) {
    try {
      if (window.getComputedStyle(clickable).cursor === 'pointer') {
        var rowLabel = '';
        if (clickable.tagName === 'TR') {
          var firstCell = clickable.querySelector('td');
          rowLabel = firstCell ? (firstCell.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 60) : '';
        } else {
          rowLabel = getLabel(clickable);
        }
        if (!rowLabel) return;
        sendEvent('click', {
          component: getSection(clickable),
          action: rowLabel,
          metadata: { element: clickable.tagName === 'TR' ? 'table-row' : 'chip', label: rowLabel },
        });
      }
    } catch(ex) {}
  }
}

export function useTracker() {
  const location = useLocation();

  useEffect(() => {
    sendEvent('page_view', { page: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    var sid = getSessionId();
    function ping() {
      if (viewingAs()) return; // no live session for the person while a superadmin views as them
      fetch(TRACKER_URL + '/track/ping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
        body: JSON.stringify({ session_id: sid }),
        keepalive: true,
      }).catch(function() {});
    }
    ping();
    var interval = setInterval(ping, 30000);
    function handleUnload() {
      if (viewingAs()) return;
      fetch(TRACKER_URL + '/track/session-end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
        body: JSON.stringify({ session_id: sid }),
        keepalive: true,
      }).catch(function() {});
    }
    window.addEventListener('beforeunload', handleUnload);
    return function() { clearInterval(interval); window.removeEventListener('beforeunload', handleUnload); };
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClick, true);
    return function() { document.removeEventListener('click', handleClick, true); };
  }, []);

  var track = useCallback(function(eventType, opts) { sendEvent(eventType, opts); }, []);
  return { track: track };
}
